import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { chatsApi } from '@/api/chats'
import { streamChatRunEvents, workflowRunsHitlApi } from '@/api/workflowRunsHitl'
import { workflowRunsApi } from '@/api/workflowRuns'
import { useTenantKey } from './useTenantKey'

export interface PendingUserInputRequest {
  runId: string
  nodeId: string
  nodeName: string
  requestId: string
  context: string | null
  questionsJson: string
}

export type NodeStatus = string

export interface ChatRunStreamState {
  activeRunId: string | null
  nodeStatuses: Record<string, NodeStatus>
  nodeTypes: Record<string, string>
  runStatus: string | null
  pendingUserInputRequest: PendingUserInputRequest | null
}

type Action =
  | { type: 'SET_ACTIVE_RUN'; runId: string | null }
  | { type: 'NODE_STATE_CHANGED'; nodeId: string; status: NodeStatus; taskType?: string }
  | { type: 'RUN_STATE_CHANGED'; status: string }
  | { type: 'NODE_USER_INPUT_REQUESTED'; payload: PendingUserInputRequest }
  | { type: 'USER_INPUT_RESOLVED' }
  | { type: 'RESET' }

const initialState: ChatRunStreamState = {
  activeRunId: null,
  nodeStatuses: {},
  nodeTypes: {},
  runStatus: null,
  pendingUserInputRequest: null,
}

function reducer(state: ChatRunStreamState, action: Action): ChatRunStreamState {
  switch (action.type) {
    case 'SET_ACTIVE_RUN':
      return { ...initialState, activeRunId: action.runId }
    case 'NODE_STATE_CHANGED':
      return {
        ...state,
        nodeStatuses: { ...state.nodeStatuses, [action.nodeId]: action.status },
        nodeTypes: action.taskType
          ? { ...state.nodeTypes, [action.nodeId]: action.taskType }
          : state.nodeTypes,
      }
    case 'RUN_STATE_CHANGED':
      return { ...state, runStatus: action.status }
    case 'NODE_USER_INPUT_REQUESTED':
      return { ...state, pendingUserInputRequest: action.payload }
    case 'USER_INPUT_RESOLVED':
      return { ...state, pendingUserInputRequest: null }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

export function useChatRunStream(projectId: string, chatId: string | null) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const abortRef = useRef<AbortController | null>(null)
  const resolvedRequestIdsRef = useRef<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  const { data: runs } = useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId ?? '', 'runs'),
    queryFn: () => chatsApi.getRuns(projectId, chatId!),
    enabled: !!chatId && !!projectId,
    staleTime: 0,
    refetchInterval: 3_000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!chatId) {
      dispatch({ type: 'RESET' })
      return
    }
    if (!runs) return
    const active = runs.find((r) => !r.isTerminal) ?? runs[0]
    dispatch({ type: 'SET_ACTIVE_RUN', runId: active ? active.runId : null })
  }, [chatId, runs])

  useEffect(() => {
    resolvedRequestIdsRef.current = new Set()
  }, [state.activeRunId])

  useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', state.activeRunId ?? '', 'detail-seed'),
    queryFn: async () => {
      const detail = await workflowRunsApi.getById(projectId, state.activeRunId!)
      for (const phase of detail.phases) {
        for (const branch of phase.branches) {
          for (const node of branch.nodes) {
            dispatch({
              type: 'NODE_STATE_CHANGED',
              nodeId: node.id,
              status: node.status,
              taskType: node.taskType,
            })
          }
        }
      }
      return detail
    },
    enabled: !!projectId && !!state.activeRunId,
    staleTime: 30_000,
  })

  const { data: pendingFromServer } = useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', state.activeRunId ?? '', 'pending-user-input'),
    queryFn: () => workflowRunsHitlApi.getPendingUserInput(projectId, state.activeRunId!),
    enabled: !!projectId && !!state.activeRunId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 3_000,
  })

  useEffect(() => {
    if (!pendingFromServer) {
      if (state.pendingUserInputRequest && pendingFromServer === null) {
        dispatch({ type: 'USER_INPUT_RESOLVED' })
      }
      return
    }
    if (resolvedRequestIdsRef.current.has(pendingFromServer.requestId)) return
    if (state.pendingUserInputRequest?.requestId === pendingFromServer.requestId) return

    dispatch({
      type: 'NODE_USER_INPUT_REQUESTED',
      payload: {
        runId: pendingFromServer.runId,
        nodeId: pendingFromServer.nodeId,
        nodeName: pendingFromServer.nodeName,
        requestId: pendingFromServer.requestId,
        context: pendingFromServer.context,
        questionsJson: pendingFromServer.questionsJson,
      },
    })
  }, [pendingFromServer, state.pendingUserInputRequest])

  useEffect(() => {
    if (!chatId || !state.activeRunId) return

    abortRef.current?.abort()
    abortRef.current = new AbortController()
    const signal = abortRef.current.signal

    const invalidateRunViews = () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', chatId, 'runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    }

    const run = async () => {
      try {
        await streamChatRunEvents(
          projectId,
          chatId,
          state.activeRunId!,
          (event, data) => {
            try {
              const parsed = JSON.parse(data)
              switch (event) {
                case 'node_user_input_requested': {
                  let inner: { requestId?: string; nodeName?: string; context?: string; questions?: unknown } | null = null
                  try {
                    inner = typeof parsed.metadataJson === 'string'
                      ? JSON.parse(parsed.metadataJson)
                      : parsed.metadataJson ?? null
                  } catch {
                    inner = null
                  }
                  if (!inner?.requestId) break
                  dispatch({
                    type: 'NODE_USER_INPUT_REQUESTED',
                    payload: {
                      runId: state.activeRunId!,
                      nodeId: parsed.entityId ?? '',
                      nodeName: inner.nodeName ?? '',
                      requestId: inner.requestId,
                      context: inner.context ?? null,
                      questionsJson: typeof inner.questions === 'string' ? inner.questions : JSON.stringify(inner.questions ?? []),
                    },
                  })
                  if (parsed.entityId) {
                    dispatch({ type: 'NODE_STATE_CHANGED', nodeId: parsed.entityId, status: 'AwaitingUserInput' })
                  }
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  break
                }
                case 'node_user_input_resolved':
                  dispatch({ type: 'USER_INPUT_RESOLVED' })
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  break
                case 'node_dispatched':
                case 'node_started':
                case 'node_completed':
                case 'node_failed':
                case 'node_skipped':
                case 'node_retry_pending':
                case 'node_auto_retry_scheduled': {
                  let meta: { taskType?: string } = {}
                  try {
                    meta = typeof parsed.metadataJson === 'string'
                      ? JSON.parse(parsed.metadataJson)
                      : parsed.metadataJson ?? {}
                  } catch {
                    meta = {}
                  }
                  dispatch({
                    type: 'NODE_STATE_CHANGED',
                    nodeId: parsed.entityId ?? parsed.nodeId ?? '',
                    status: parsed.status ?? '',
                    taskType: meta.taskType,
                  })
                  break
                }
                case 'run_started':
                case 'run_completed':
                case 'run_failed':
                case 'run_cancelled':
                  dispatch({ type: 'RUN_STATE_CHANGED', status: parsed.status ?? event })
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  invalidateRunViews()
                  break
                case 'phase_started':
                case 'phase_awaiting_approval':
                case 'phase_approved':
                case 'phase_rejected':
                case 'phase_completed':
                case 'phase_failed':
                case 'change_set_staged':
                case 'change_set_applied':
                case 'change_set_discarded':
                case 'change_set_superseded':
                case 'gate_feedback_submitted':
                  invalidateRunViews()
                  break
                case 'chat_message_added':
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  break
                case 'heartbeat':
                default:
                  break
              }
            } catch {
            }
          },
          signal,
        )
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Chat run stream failed:', err)
        }
      }
    }

    void run()

    return () => {
      abortRef.current?.abort()
    }
  }, [projectId, chatId, state.activeRunId, queryClient, tenantKey])

  const clearPendingUserInput = useCallback(() => {
    const resolvedId = state.pendingUserInputRequest?.requestId
    if (resolvedId) {
      resolvedRequestIdsRef.current.add(resolvedId)
    }
    dispatch({ type: 'USER_INPUT_RESOLVED' })
    if (state.activeRunId) {
      queryClient.invalidateQueries({
        queryKey: tenantKey('projects', projectId, 'workflow-runs', state.activeRunId, 'pending-user-input'),
      })
    }
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId, 'chats', chatId ?? '', 'messages'),
    })
  }, [state.activeRunId, state.pendingUserInputRequest, queryClient, tenantKey, projectId, chatId])

  const activeRun = state.activeRunId ? runs?.find((r) => r.runId === state.activeRunId) : undefined
  const isLlmNodeActive = Object.entries(state.nodeStatuses).some(([nodeId, status]) =>
    status === 'Running' && (state.nodeTypes[nodeId] === 'claude-code' || state.nodeTypes[nodeId] === 'copilot-cli'),
  )
  const isActiveRunRunning =
    !!activeRun
    && !activeRun.isTerminal
    && isLlmNodeActive
    && !state.pendingUserInputRequest

  return { ...state, clearPendingUserInput, isActiveRunRunning }
}
